import { useMemo, useState } from 'react'
import { Inventory2Outlined as Inventory2OutlinedIcon } from '@mui/icons-material'
import {
  useQueryInventoryRecipeByItem,
  useQueryInventoryRecipeComponents,
  useQueryInventoryRecipesByItems
} from '../../../../../../../queries/inventoryQueries'
import { getStatusLabel } from '../../../../../../../constants/inventoryLabels'
import SectionCard from '../../components/SectionCard'
import {
  createRecipeComponentsState,
  createRecipeFormState
} from '../../hooks/useInventoryPageState'
import { formatRecipeQuantityFromBase, getDefaultRecipeDisplayUnit } from '../../utils/inventoryUnits'
import { getReadableUnitLabel, resolveRecipeId, toBoolean } from '../../utils/inventoryResolvers'
import RecipeBuildView from './RecipeBuildView'
import RecipeEditorModal from './RecipeEditorModal'
import RecipeListView from './RecipeListView'
import RecipeReviewView from './RecipeReviewView'
import {
  buildRecipeComponentsFromRows,
  buildRecipeFormFromRecipe,
  getUnitContextText
} from './recipeSection.utils'

const RecipeEmptyState = ({ navigation }) => (
  <SectionCard
    title='Construir receta'
    description='Primero necesitas tener al menos un producto marcado como Producto con receta.'
  >
    <div className='rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center'>
      <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-accent2'>
        <Inventory2OutlinedIcon />
      </div>
      <h4 className='mt-3 text-lg font-semibold text-slate-900'>Aún no hay productos listos para receta</h4>
      <p className='mt-2 text-sm leading-5 text-slate-500'>
        Ve al catálogo y crea o edita un producto con el tipo{' '}
        <span className='font-semibold text-slate-700'>Producto con receta</span>.
      </p>
      <button
        type='button'
        onClick={() => navigation.navigateToSection('items')}
        className='mt-5 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800'
      >
        Ir a catálogo
      </button>
    </div>
  </SectionCard>
)

const RecipesSection = ({ actions, businessId, data, navigation, state }) => {
  const [modalView, setModalView] = useState('closed')
  const [tableSearch, setTableSearch] = useState('')
  const [reviewSearch, setReviewSearch] = useState('')
  const [reviewSelectedItemId, setReviewSelectedItemId] = useState('')

  const bomItemIds = useMemo(
    () => data.bomItemOptions.map(option => Number(option.id)).filter(itemId => itemId > 0),
    [data.bomItemOptions]
  )

  const recipesByItemsQueries = useQueryInventoryRecipesByItems(businessId, bomItemIds, {
    enabled: !!businessId && bomItemIds.length > 0
  })

  const selectedBomOption = useMemo(
    () =>
      data.bomItemOptions.find(option => String(option.id) === String(state.selectedBomItemId)) || null,
    [data.bomItemOptions, state.selectedBomItemId]
  )

  const reviewSelectedOption = useMemo(
    () => data.bomItemOptions.find(option => String(option.id) === String(reviewSelectedItemId)) || null,
    [data.bomItemOptions, reviewSelectedItemId]
  )

  const reviewRecipeQuery = useQueryInventoryRecipeByItem(
    businessId,
    Number(reviewSelectedItemId || 0),
    {
      enabled: modalView === 'review' && !!businessId && !!reviewSelectedItemId
    }
  )

  const reviewSelectedRecipe = reviewRecipeQuery.data
  const reviewSelectedRecipeId = resolveRecipeId(reviewSelectedRecipe)

  const reviewRecipeComponentsQuery = useQueryInventoryRecipeComponents(
    businessId,
    reviewSelectedRecipeId,
    {
      enabled: modalView === 'review' && !!businessId && !!reviewSelectedRecipeId
    }
  )

  const reviewRecipeComponentsRows = reviewRecipeComponentsQuery?.data?.rows || []

  const finalProductUnitLabel = selectedBomOption ? getUnitContextText(selectedBomOption.unit) : ''
  const finalProductSingularUnitLabel = selectedBomOption
    ? getReadableUnitLabel(selectedBomOption.unit, 1)
    : 'unidad'

  const recipeDraftIngredients = useMemo(
    () =>
      state.recipeComponents
        .map((component, index) => {
          const selectedOption =
            data.itemOptions.find(option => String(option.id) === String(component.componentItemId)) || null

          return {
            ...component,
            index,
            label: selectedOption?.label || '',
            unit: selectedOption?.unit || ''
          }
        })
        .filter(component => component.componentItemId && component.qtyRequired),
    [data.itemOptions, state.recipeComponents]
  )

  const filteredReviewOptions = useMemo(() => {
    const normalizedSearch = reviewSearch.trim().toLowerCase()
    if (!normalizedSearch) return data.bomItemOptions

    return data.bomItemOptions.filter(row => {
      const haystack = `${row.sku || ''} ${row.name || ''} ${row.label || ''}`.toLowerCase()
      return haystack.includes(normalizedSearch)
    })
  }, [data.bomItemOptions, reviewSearch])

  const recipeTableRows = useMemo(
    () =>
      data.bomItemOptions.map((option, index) => {
        const recipe = recipesByItemsQueries[index]?.data || null
        const recipeId = resolveRecipeId(recipe)
        const isActive = recipe
          ? toBoolean(recipe?.active ?? recipe?.is_active ?? recipe?.isActive ?? true)
          : false

        return {
          id: option.id,
          recipeId,
          sku: option.sku || '',
          name: option.name || 'Producto sin nombre',
          unitContext: `Unidad final: ${getUnitContextText(option.unit)}`,
          hasRecipe: !!recipeId,
          isActive,
          statusLabel: recipe ? getStatusLabel(isActive) : 'Sin receta',
          versionLabel: recipe?.version ? `v${recipe.version}` : '-',
          yieldLabel: recipe
            ? formatRecipeQuantityFromBase(recipe?.yield_qty ?? recipe?.yieldQty, option.unit)
            : '-'
        }
      }),
    [data.bomItemOptions, recipesByItemsQueries]
  )

  const createdRecipeRows = useMemo(
    () => recipeTableRows.filter(row => row.hasRecipe),
    [recipeTableRows]
  )

  const filteredRecipeTableRows = useMemo(() => {
    const normalizedSearch = tableSearch.trim().toLowerCase()
    if (!normalizedSearch) return createdRecipeRows

    return createdRecipeRows.filter(row => {
      const haystack = `${row.recipeId || ''} ${row.sku || ''} ${row.name || ''}`.toLowerCase()
      return haystack.includes(normalizedSearch)
    })
  }, [createdRecipeRows, tableSearch])

  const recipesTableLoading = recipesByItemsQueries.some(query => query.isLoading)

  const openBuildView = (itemId = '', recipe = null, recipeComponentsRows = []) => {
    state.setSelectedBomItemId(String(itemId || ''))

    if (recipe) {
      state.setRecipeForm(buildRecipeFormFromRecipe(recipe))
      state.setRecipeComponents(buildRecipeComponentsFromRows(recipeComponentsRows, data.itemOptions))
    } else {
      state.setRecipeForm(createRecipeFormState())
      state.setRecipeComponents(createRecipeComponentsState())
    }

    setModalView('build')
  }

  const handleOpenBuildModal = () => {
    openBuildView('')
  }

  const handleOpenReviewModal = itemId => {
    setReviewSelectedItemId(String(itemId))
    setReviewSearch('')
    setModalView('review')
  }

  const handleCloseModal = () => {
    setModalView('closed')
  }

  const handleBuilderProductChange = event => {
    const nextValue = event.target.value
    if (nextValue === state.selectedBomItemId) return

    state.setSelectedBomItemId(nextValue)
    state.setRecipeForm(createRecipeFormState())
    state.setRecipeComponents(createRecipeComponentsState())
  }

  const handleReviewSelectItem = itemId => {
    setReviewSelectedItemId(itemId)
  }

  const handleOpenCurrentRecipe = () => {
    if (!state.selectedBomItemId) return
    setReviewSelectedItemId(String(state.selectedBomItemId))
    setModalView('review')
  }

  const handleBuildFromReview = () => {
    if (!reviewSelectedItemId) return
    openBuildView(reviewSelectedItemId, reviewSelectedRecipe, reviewRecipeComponentsRows)
  }

  const handleRecipeIngredientChange = (index, nextItemId) => {
    const selectedOption =
      data.recipeComponentOptions.find(option => String(option.id) === String(nextItemId)) || null
    const nextDisplayUnit = getDefaultRecipeDisplayUnit(selectedOption?.unit || '')

    state.setRecipeComponents(prev =>
      prev.map((component, componentIndex) =>
        componentIndex === index
          ? {
              ...component,
              componentItemId: nextItemId,
              qtyRequired: '',
              displayQuantity: '',
              displayUnit: nextDisplayUnit
            }
          : component
      )
    )
  }

  const handleRecipeQuantityChange = (index, rawValue) => {
    state.setRecipeComponents(prev =>
      prev.map((component, componentIndex) => {
        if (componentIndex !== index) return component

        return {
          ...component,
          displayQuantity: rawValue,
          qtyRequired: rawValue
        }
      })
    )
  }

  const handleRecipeDisplayUnitChange = (index, nextDisplayUnit) => {
    state.setRecipeComponents(prev =>
      prev.map((component, componentIndex) => {
        if (componentIndex !== index) return component

        return {
          ...component,
          displayUnit: nextDisplayUnit,
          displayQuantity: component.displayQuantity || component.qtyRequired
        }
      })
    )
  }

  const handleIngredientFieldChange = (index, field, value) => {
    if (field === 'componentItemId') {
      handleRecipeIngredientChange(index, value)
      return
    }

    if (field === 'displayQuantity') {
      handleRecipeQuantityChange(index, value)
      return
    }

    if (field === 'displayUnit') {
      handleRecipeDisplayUnitChange(index, value)
      return
    }

    actions.handleRecipeComponentChange(index, field, value)
  }

  if (data.bomItemOptions.length === 0) {
    return <RecipeEmptyState navigation={navigation} />
  }

  return (
    <>
      <RecipeListView
        rows={filteredRecipeTableRows}
        loading={recipesTableLoading}
        searchValue={tableSearch}
        onSearchChange={setTableSearch}
        onCreate={handleOpenBuildModal}
        onOpenReview={handleOpenReviewModal}
      />

      <RecipeEditorModal open={modalView !== 'closed'} view={modalView} onClose={handleCloseModal}>
        {modalView === 'build' ? (
          <RecipeBuildView
            actions={actions}
            data={data}
            state={state}
            selectedBomOption={selectedBomOption}
            recipeDraftIngredients={recipeDraftIngredients}
            finalProductUnitLabel={finalProductUnitLabel}
            finalProductSingularUnitLabel={finalProductSingularUnitLabel}
            onBuilderProductChange={handleBuilderProductChange}
            onIngredientFieldChange={handleIngredientFieldChange}
            onOpenCurrentRecipe={handleOpenCurrentRecipe}
          />
        ) : (
          <RecipeReviewView
            filteredReviewOptions={filteredReviewOptions}
            recipeSearch={reviewSearch}
            onSearchChange={setReviewSearch}
            selectedItemId={reviewSelectedItemId}
            onSelectItem={handleReviewSelectItem}
            reviewRecipeQuery={reviewRecipeQuery}
            reviewRecipeComponentsQuery={reviewRecipeComponentsQuery}
            itemOptions={data.itemOptions}
            reviewSelectedOption={reviewSelectedOption}
            reviewSelectedRecipe={reviewSelectedRecipe}
            reviewRecipeComponentsRows={reviewRecipeComponentsRows}
            onBuildFromReview={handleBuildFromReview}
          />
        )}
      </RecipeEditorModal>
    </>
  )
}

export default RecipesSection
